import axiosNest from '../../api/axiosNest';

/**
 * Parametry kosztu dolotu spółki (I.02) — odpowiada `PUT /companies/:id/cost-parameters`.
 *
 * Wszystkie stawki podaje Marathon. Puste pole = parametr nieznany, a wtedy
 * koszt dolotu w podpowiedziach też jest nieznany (nie zerowy).
 *
 * Stawki pieniężne mają walutę (30.09.2026: część Marathon podał w EUR) —
 * `currencyKey` to pole waluty obok wartości. EUR API przelicza kursem
 * średnim NBP (tabela A) z dnia realizacji zlecenia.
 */
export const COST_CURRENCIES = ['PLN', 'EUR'] as const;
export type CostCurrency = (typeof COST_CURRENCIES)[number];

export const COST_FIELDS = [
  {
    key: 'costDriverHour',
    label: 'Koszt godziny pracy kierowcy',
    unit: '/h',
    currencyKey: 'costDriverHourCurrency',
  },
  {
    key: 'costTractorMonth',
    label: 'Koszt utrzymania ciągnika (zestawu)',
    unit: '/mies.',
    currencyKey: 'costTractorMonthCurrency',
  },
  { key: 'costTractorHoursPerMonth', label: 'Godziny pracy ciągnika w miesiącu', unit: 'h', currencyKey: null },
  { key: 'fuelConsumptionL100km', label: 'Średnie spalanie', unit: 'l/100 km', currencyKey: null },
  { key: 'fuelPricePerL', label: 'Cena paliwa', unit: '/l', currencyKey: 'fuelPriceCurrency' },
] as const;

export type CostKey = (typeof COST_FIELDS)[number]['key'];
export type CurrencyKey = NonNullable<(typeof COST_FIELDS)[number]['currencyKey']>;
export type CostValues = Record<CostKey, number | null>;

/** Liczba dodatnia z rekordu albo `null` — to samo kryterium co reguła w API. */
export function costValue(record: Record<string, unknown>, key: CostKey): number | null {
  const v = record[key];
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
}

/** Waluta stawki z rekordu; brak albo nieznana = PLN (domyślna kolumny). */
export function costCurrency(record: Record<string, unknown>, key: CurrencyKey): CostCurrency {
  const v = record[key];
  return (COST_CURRENCIES as readonly unknown[]).includes(v) ? (v as CostCurrency) : 'PLN';
}

/** Jednostka pola po ludzku: „zł/h”, „€/mies.”, „l/100 km”. */
export function unitLabel(unit: string, currency: CostCurrency | null): string {
  if (currency === null) return unit;
  return `${currency === 'EUR' ? '€' : 'zł'}${unit}`;
}

export function missingCount(record: Record<string, unknown>): number {
  return COST_FIELDS.filter((f) => costValue(record, f.key) === null).length;
}

/**
 * Dodatki zapisu przy przełączniku `parameterOwnership` (24.10.2026,
 * S-24.10-12): powód (wymagany), „obowiązuje od” (chwila z przesunięciem;
 * brak = od razu) i właściciel stawek (`null` = nieustalony).
 */
export type CostRatesExtras = {
  reason: string;
  validFrom?: string | null;
  owner?: string | null;
};

export async function saveCostParameters(
  companyId: number | string,
  values: Partial<Record<CostKey, number | null> & Record<CurrencyKey, CostCurrency>>,
  extras?: CostRatesExtras,
): Promise<void> {
  const body: Record<string, unknown> = { ...values };
  if (extras) {
    body.reason = extras.reason;
    if (extras.validFrom) body.validFrom = extras.validFrom;
    if (extras.owner !== undefined) body.owner = extras.owner;
  }
  await axiosNest.put(`/companies/${companyId}/cost-parameters`, body);
}

/** Przełącznik stawek z historią — klucz z rejestru API (dane). */
export const FEATURE_COST_RATES_HISTORY = 'parameterOwnership';

/** Zmiana zaplanowana stawki — pole `costRatesScheduled` spółki. */
export type ScheduledCostRate = {
  id: number;
  field: string;
  label: string;
  newValue: number | null;
  newText: string | null;
  validFrom: string;
};

/** Wpis historii stawek — `GET /companies/:id/cost-rate-history`. */
export type CostRateChange = {
  id: number;
  field: string;
  label: string;
  oldValue: number | null;
  newValue: number | null;
  oldText: string | null;
  newText: string | null;
  reason: string;
  validFrom: string;
  /** `null` = zaplanowana (czeka) albo odwołana. */
  appliedAt: string | null;
  cancelledAt: string | null;
  cancelledByName: string | null;
  cancelReason: string | null;
  changedAt: string;
  changedByName: string | null;
};

export async function fetchCostRateHistory(companyId: number | string): Promise<CostRateChange[]> {
  const { data } = await axiosNest.get<CostRateChange[]>(
    `/companies/${companyId}/cost-rate-history`,
  );
  return Array.isArray(data) ? data : [];
}

export async function cancelCostRateChange(
  companyId: number | string,
  changeId: number,
  reason: string,
): Promise<void> {
  await axiosNest.post(`/companies/${companyId}/cost-rate-changes/${changeId}/cancel`, {
    reason,
  });
}

/** Zaplanowane zmiany stawek z rekordu spółki (tylko przy przełączniku). */
export function scheduledCostRates(record: Record<string, unknown>): ScheduledCostRate[] {
  const v = record.costRatesScheduled;
  return Array.isArray(v) ? (v as ScheduledCostRate[]) : [];
}

/** Wartość w historii: liczba z przecinkiem, kod waluty albo „brak” (nie zero). */
export function costRateValueText(value: number | null, text: string | null): string {
  if (text !== null) return text;
  return value === null ? 'brak' : value.toLocaleString('pl-PL');
}

/** Stan wpisu historii słowem. */
export function costRateChangeState(c: Pick<CostRateChange, 'appliedAt' | 'cancelledAt'>): string {
  if (c.cancelledAt) return 'odwołana';
  return c.appliedAt ? 'obowiązuje' : 'zaplanowana';
}
